import { ref, watch, onUnmounted, type Ref } from 'vue'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ')

/**
 * Traps focus within a container element while active.
 * Restores focus to the previously focused element on deactivation.
 *
 * Usage:
 *   const { trapRef, activate, deactivate } = useFocusTrap()
 *   // Bind trapRef to the modal container element
 *   // Call activate() when modal opens, deactivate() when it closes
 *
 * Or with auto-activation via a reactive boolean:
 *   const { trapRef } = useFocusTrap(isModalOpen)
 */
export function useFocusTrap(isActive?: Ref<boolean>) {
  const trapRef = ref<HTMLElement | null>(null)
  let previousFocus: HTMLElement | null = null
  let active = false

  function getFocusableElements(): HTMLElement[] {
    if (!trapRef.value) return []
    return Array.from(trapRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .filter(el => el.offsetParent !== null) // visible only
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !active) return

    const focusable = getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  function activate() {
    if (active) return
    active = true
    previousFocus = document.activeElement as HTMLElement

    document.addEventListener('keydown', handleKeydown)

    // Focus the first focusable element on next tick
    requestAnimationFrame(() => {
      const focusable = getFocusableElements()
      if (focusable.length > 0) {
        focusable[0].focus()
      } else {
        trapRef.value?.focus()
      }
    })
  }

  function deactivate() {
    if (!active) return
    active = false
    document.removeEventListener('keydown', handleKeydown)

    // Restore focus
    if (previousFocus && previousFocus.isConnected) {
      previousFocus.focus()
    }
    previousFocus = null
  }

  // Auto-activate/deactivate based on reactive boolean
  if (isActive) {
    watch(isActive, (val) => {
      if (val) {
        // Delay activation to allow DOM to render
        requestAnimationFrame(() => activate())
      } else {
        deactivate()
      }
    })
  }

  onUnmounted(() => {
    deactivate()
  })

  return { trapRef, activate, deactivate }
}
